// The UI follows the browser's language, with a per-app override (Sistema / Español / English) —
// the same three choices the native profiles offer (docs/PARITY.md "UI language"). Wording comes
// from the Apple tables (scripts/gen-strings.mjs) plus the few web-only keys in ./web.ts, so a
// sentence can never read differently here than on the Mac beside it.
import es from './generated/es.json';
import en from './generated/en.json';
import { webStrings } from './web';

export type Language = 'es' | 'en';
export type LanguageChoice = 'system' | Language;

const LANGUAGE_KEY = 'satori.language';

const tables: Record<Language, Record<string, string>> = {
  es: { ...(es as Record<string, string>), ...webStrings.es },
  en: { ...(en as Record<string, string>), ...webStrings.en },
};

export function systemLanguage(): Language {
  const langs = (typeof navigator !== 'undefined' && navigator.languages) || ['es'];
  for (const tag of langs) {
    const base = tag.toLowerCase().split('-')[0];
    if (base === 'es') return 'es';
    if (base === 'en') return 'en';
  }
  return 'es';
}

export function storedLanguageChoice(): LanguageChoice {
  try {
    const v = localStorage.getItem(LANGUAGE_KEY);
    if (v === 'es' || v === 'en') return v;
  } catch {
    /* storage unavailable: follow the system */
  }
  return 'system';
}

export function storeLanguageChoice(choice: LanguageChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(LANGUAGE_KEY);
    else localStorage.setItem(LANGUAGE_KEY, choice);
  } catch {
    /* a lost preference only means the system language wins next time */
  }
}

let current: Language = (() => {
  const choice = storedLanguageChoice();
  return choice === 'system' ? systemLanguage() : choice;
})();

export function language(): Language {
  return current;
}

/** BCP-47 tag dates and numbers are formatted with. */
export function locale(): string {
  return current === 'es' ? 'es-MX' : 'en-US';
}

export function setLanguage(lang: Language): void {
  current = lang;
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
}

/** A user-facing string. An unknown key is a bug, shown as the key so it is noticed. */
export function t(key: string): string {
  return tables[current][key] ?? tables.es[key] ?? key;
}

/**
 * A string with arguments, in the Apple table's own placeholder grammar: `%@` / `%d` take the
 * next argument, `%1$@` / `%2$d` a positional one, `%%` is a percent sign.
 */
export function tf(key: string, ...args: Array<string | number>): string {
  return format(t(key), args);
}

export function format(template: string, args: Array<string | number>): string {
  let next = 0;
  return template.replace(/%(?:(\d+)\$)?([@dfl]+|%)/g, (_m, pos: string | undefined, kind: string) => {
    if (kind === '%') return '%';
    const index = pos ? Number(pos) - 1 : next++;
    const value = args[index];
    return value === undefined ? '' : String(value);
  });
}

/** Test seam: run with a given language. */
export function withLanguage<T>(lang: Language, body: () => T): T {
  const saved = current;
  current = lang;
  try {
    return body();
  } finally {
    current = saved;
  }
}
